
import { getApi } from "./resonanceApi";
import { AxiosResponse } from 'axios';
import { RoleKeyEnum, WelcomeType } from "../types/welcome";
import { ProfileType } from "../types/profile";
import { RoleEnum } from "../types/invitation.types";

export enum CommonApiUrlsEnum {
  GET_WELCOME_INFOS = 'studies/welcome/{slug}'
}

// Bump when the cached welcome SHAPE changes so stale entries are ignored. v2: roles.subject
// became an array (multi-subject). A page loaded with the new backend but the pre-array
// frontend cached a malformed subject (an array spread into an object, no `access`) under the
// old key — versioning makes those unreadable so they can't crash the landing.
const WELCOME_CACHE_VERSION = 'v2';
const welcomeCacheKey = (slug: string): string => `welcome:${WELCOME_CACHE_VERSION}:${slug}`;

// Synchronously read the last public welcome payload persisted for a study slug, so the
// landing can render instantly from cache while the network request revalidates in the
// background. Returns undefined on a miss or unparseable entry.
export const getCachedWelcome = (slug?: string): WelcomeType | undefined => {
  if (!slug) return undefined;
  try {
    const raw = localStorage.getItem(welcomeCacheKey(slug));
    return raw ? (JSON.parse(raw) as WelcomeType) : undefined;
  } catch {
    return undefined;
  }
};

// Dedupe concurrent identical welcome requests (e.g. ConfigContext + AuthContext firing
// together on a multi-study session restore) so they share a single network round-trip.
const inflightWelcome = new Map<string, Promise<AxiosResponse<WelcomeType>>>();

const fetchWelcomeInfo = async ({ slug, lang, invitation_token }: { slug: string, lang: string, invitation_token?: string }): Promise<AxiosResponse<WelcomeType>> => {
  const url = CommonApiUrlsEnum.GET_WELCOME_INFOS;
  const response = await getApi<WelcomeType>(url, { slug }, {
    lang, invitation_token
  });

  const roles: ProfileType[] = [
    ...(response.data.roles[RoleKeyEnum.SUBJECT] ?? []).map(role => ({ ...role, name: RoleEnum.SUBJECT })),
    ...response.data.roles[RoleKeyEnum.INVESTIGATOR].map(role => ({ ...role, name: RoleEnum.INVESTIGATOR })),
    ...response.data.roles[RoleKeyEnum.INTERVENORS].map(role => ({ ...role, name: RoleEnum.INTERVENOR }))
  ]

  const result = {
    ...response,
    data: {
      ...response.data,
      roles
    }
  };

  // Persist only the public payload — the invitation-token variant carries PII.
  if (!invitation_token && slug) {
    try {
      localStorage.setItem(welcomeCacheKey(slug), JSON.stringify(result.data));
    } catch {
      // best-effort cache: ignore quota / serialization errors
    }
  }

  return result;
}

export const getWelcomeInfo = async (params: { slug: string, lang: string, invitation_token?: string }): Promise<AxiosResponse<WelcomeType>> => {
  const key = `${params.slug}|${params.lang}|${params.invitation_token ?? ''}`;
  const existing = inflightWelcome.get(key);
  if (existing) return existing;

  const promise = fetchWelcomeInfo(params);
  inflightWelcome.set(key, promise);
  try {
    return await promise;
  } finally {
    inflightWelcome.delete(key);
  }
}
